import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { attractChannels, convertActions, journey, journeyClosing } from '../../../data/deck';
import { heroOct, heroPromise } from '../../../data/teaser';
import type { StageId } from '../../../data/deck';
export function JourneySlide() {
  return <Slide id="journey" data-id="element-189" data-mp="125uvhzd8vjon">
      <InView data-id="element-190" data-mp="126crqvzwpxwk">
        <SlideHeadline lead="From attention" follow="to action." data-id="element-191" data-mp="126w8wahqgi9j" />
      </InView>

      <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:gap-6" data-id="element-192" data-mp="xci5plwtx6ub">
        {journey.map((stage, i) => <React.Fragment key={stage.id} data-id="element-193" data-mp="scnknyzqjbh6" data-mpi={i}>
            <InView delay={0.1 + i * 0.06} data-id="element-194" data-mp="ngyf9f11b0cy">
              <div className="flex h-[280px] items-center justify-center rounded-[28px] bg-canvas2" data-id="element-195" data-mp="ndsrq48206jc">
                <StageVisual id={stage.id} data-id="element-196" data-mp="iin5136tunu7" />
              </div>
              <div className="mt-6 flex items-baseline gap-3" data-id="element-197" data-mp="egick7s47x95">
                <span className="text-[10px] tabular-nums tracking-[0.2em] text-navy/40" data-id="element-198" data-mp="2g9f1xeecxgi7">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-display text-[30px] font-semibold uppercase leading-none tracking-[0.02em] text-navy" data-id="element-199" data-mp="2l2qc9pidofev">
                  {stage.name}
                </h3>
              </div>
              <p className="mt-3 text-[13.5px] leading-relaxed text-navy" data-id="element-200" data-mp="2b8rc2w1v75pk">{stage.channels}</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-navy/55" data-id="element-201" data-mp="2z17sl25yepmr">{stage.goal}</p>
            </InView>
            {i < journey.length - 1 && <span aria-hidden="true" className="hidden h-[280px] items-center text-navy/35 lg:flex" data-id="element-202" data-mp="2q0i2mf3djtgy">
                <ArrowRightIcon className="h-5 w-5" data-id="element-203" data-mp="2u0k2qjtgoeu4" />
              </span>}
          </React.Fragment>)}
      </div>

      <InView delay={0.3} data-id="element-204" data-mp="1ej9zdx4gsjed">
        <p className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-navy/[0.12] pt-8 font-display text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] text-navy sm:text-[32px]" data-id="element-205" data-mp="1im6kpvle2tm0">
          {journeyClosing.map((line, i) => <React.Fragment key={line} data-id="element-206" data-mp="3gb1z0eehjksm" data-mpi={i}>
              {i > 0 && <span aria-hidden="true" className="h-2 w-2 rounded-full bg-mint" data-id="element-207" data-mp="3bj0ooy0vo458" />}
              <span data-id="element-208" data-mp="3bgo0420m6vj7">{line}</span>
            </React.Fragment>)}
        </p>
      </InView>
    </Slide>;
}
function StageVisual({
  id
}: {
  id: StageId;
}) {
  if (id === 'attract') {
    return <div className="flex flex-col items-center gap-3" data-id="element-209" data-mp="32fzp7g6ipbaj">
        <div className="w-[220px] overflow-hidden rounded-[16px] bg-navy shadow-[0_20px_40px_-24px_rgba(11,42,74,0.6)]" data-id="element-210" data-mp="2xig5i0r1w6b7">
          <img src={heroOct.src} alt={heroOct.alt} className="block h-[110px] w-full object-cover" data-id="element-211" data-mp="2sm8zy92kgwts" />
          <p className="px-4 py-3 font-display text-[14px] font-semibold leading-snug text-paper" data-id="element-212" data-mp="2nn67do3rwj2p">{heroPromise}</p>
        </div>
        <div className="flex max-w-[240px] flex-wrap justify-center gap-1.5" data-id="element-213" data-mp="ltyh5jmmgh21">
          {attractChannels.map(channel => <span key={channel} className="rounded-full bg-paper px-3 py-1 text-[10.5px] text-navy ring-1 ring-navy/10" data-id="element-214" data-mp="u9o08mf8aix4">
              {channel}
            </span>)}
        </div>
      </div>;
  }
  if (id === 'convince') {
    return <div className="flex h-[230px] w-[130px] flex-col overflow-hidden rounded-[22px] border-[5px] border-[#0A1622] bg-paper shadow-[0_24px_50px_-28px_rgba(11,42,74,0.6)]" data-id="element-215" data-mp="135dg6fxwql34">
        <img src={heroOct.src} alt="" className="block h-[90px] w-full object-cover" data-id="element-216" data-mp="bdfsnbyqtvyn" />
        <div className="flex flex-1 flex-col p-2.5" data-id="element-217" data-mp="fe83g9n9k8x1">
          <p className="font-display text-[10px] font-semibold leading-tight text-navy" data-id="element-218" data-mp="kct2w6uoyokf">One diagnosis. Four phenotypes.</p>
          <div className="mt-2 grid grid-cols-2 gap-1" aria-hidden="true" data-id="element-219" data-mp="pbw7xrao8use">
            {['bg-irf', 'bg-srf', 'bg-hrf', 'bg-band'].map(c => <span key={c} className={`h-5 rounded-[4px] ${c} opacity-80`} data-id="element-220" data-mp="1vtsu0z5ayxcv" />)}
          </div>
          <span className="mt-auto rounded-full bg-navy py-1 text-center text-[7px] font-semibold text-paper" data-id="element-221" data-mp="20svwqcxt8rho">Continue</span>
        </div>
      </div>;
  }
  return <div className="grid w-[260px] grid-cols-2 gap-2 rounded-[18px] bg-navy p-3" data-id="element-222" data-mp="34pya3udptzr9">
      {convertActions.map(action => <span key={action} className="flex min-h-[44px] items-center rounded-[10px] border border-paper/60 px-2.5 py-2 text-[11px] leading-tight text-paper" data-id="element-223" data-mp="3imbk6ifx6yvp">
          {action}
        </span>)}
    </div>;
}